import { useState } from "react";
import { Plus, Edit, Trash2, Search, ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useCities, City, CityInsert } from "@/features/locations/hooks/useCities";
import { useStates } from "@/features/locations/hooks/useStates";
import { useDebounce } from "@/hooks/useDebounce";
import { useAuth } from "@/hooks/useAuth";
import { AvisoTabelaNacional } from "@/components/AvisoTabelaNacional";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";

const PAGE_SIZE_OPTIONS = [10, 50, 100];

export default function Cities() {
  const { user } = useAuth();
  const podeEditar = user?.isAdminGlobal ?? false;
  const { states } = useStates();

  // Filtros
  const [buscaInput, setBuscaInput] = useState("");
  const [estadoFiltro, setEstadoFiltro] = useState<number | null>(null);
  const [tamanhoPagina, setTamanhoPagina] = useState(100);
  const [pagina, setPagina] = useState(1);

  const busca = useDebounce(buscaInput, 400);

  const { cities, total, totalPaginas, isLoading, isFetching, createCity, updateCity, deleteCity, isCreating, isUpdating, isDeleting, refetch } =
    useCities({ busca, estadoId: estadoFiltro, pagina, tamanhoPagina });

  const [importando, setImportando] = useState(false);

  const handleImportarIbge = async () => {
    setImportando(true);
    try {
      const { data } = await apiClient.post("/api/cidades/importar-ibge");
      toast.success(
        `IBGE sincronizado: ${data.inseridos} novas, ${data.atualizados} atualizadas (total ${data.total})`
      );
      refetch();
    } catch {
      toast.error("Falha ao sincronizar com o IBGE.");
    } finally {
      setImportando(false);
    }
  };

  // Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [editingCity, setEditingCity] = useState<City | null>(null);
  const [formData, setFormData] = useState<CityInsert>({ nome: "", codigoIbge: null, estadoId: null, isAtivo: true, ibsMun: 0 });

  const handleFilter = (value: string) => {
    setBuscaInput(value);
    setPagina(1);
  };

  const handleEstadoFiltro = (value: string) => {
    setEstadoFiltro(value && value !== "todos" ? parseInt(value) : null);
    setPagina(1);
  };

  const handleTamanhoPagina = (value: string) => {
    setTamanhoPagina(parseInt(value));
    setPagina(1);
  };

  const openNew = () => {
    setEditingCity(null);
    setFormData({ nome: "", codigoIbge: null, estadoId: estadoFiltro, isAtivo: true, ibsMun: 0 });
    setDialogOpen(true);
  };

  const openEdit = (city: City) => {
    setEditingCity(city);
    setFormData({ nome: city.nome, codigoIbge: city.codigoIbge, estadoId: city.estadoId, isAtivo: city.isAtivo, ibsMun: city.ibsMun });
    setDialogOpen(true);
  };

  const handleSubmit = () => {
    if (editingCity) {
      updateCity({ id: editingCity.id, data: formData });
    } else {
      createCity(formData);
    }
    setDialogOpen(false);
  };

  const handleDelete = () => {
    if (deleteId) {
      deleteCity(deleteId);
      setDeleteId(null);
    }
  };

  const inicio = total === 0 ? 0 : (pagina - 1) * tamanhoPagina + 1;
  const fim = Math.min(pagina * tamanhoPagina, total);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Cidades</h1>
          <p className="text-muted-foreground">Gerencie as cidades cadastradas</p>
        </div>
        {podeEditar && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleImportarIbge} disabled={importando}>
              <RefreshCw className={`h-4 w-4 mr-2 ${importando ? "animate-spin" : ""}`} />
              {importando ? "Sincronizando..." : "Sincronizar IBGE"}
            </Button>
            <Button onClick={openNew}><Plus className="h-4 w-4 mr-2" />Nova Cidade</Button>
          </div>
        )}
      </div>

      {!podeEditar && <AvisoTabelaNacional />}

      {/* Filtros */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar cidade..."
            value={buscaInput}
            onChange={(e) => handleFilter(e.target.value)}
            className="pl-9"
          />
        </div>

        <Select
          value={estadoFiltro?.toString() ?? "todos"}
          onValueChange={handleEstadoFiltro}
        >
          <SelectTrigger className="w-52">
            <SelectValue placeholder="Todos os estados" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos os estados</SelectItem>
            {states?.map((s) => (
              <SelectItem key={s.id} value={s.id.toString()}>
                {s.uf} — {s.nome}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex items-center gap-2 ml-auto">
          <span className="text-sm text-muted-foreground whitespace-nowrap">Por página:</span>
          <Select value={tamanhoPagina.toString()} onValueChange={handleTamanhoPagina}>
            <SelectTrigger className="w-20">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((n) => (
                <SelectItem key={n} value={n.toString()}>{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Tabela */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead className="w-36">Estado</TableHead>
              <TableHead className="w-32">Código IBGE</TableHead>
              <TableHead className="text-right w-32">IBS Mun. (%)</TableHead>
              <TableHead className="text-center w-20">Ativo</TableHead>
              <TableHead className="w-20">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Carregando...</TableCell></TableRow>
            ) : cities.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Nenhuma cidade encontrada</TableCell></TableRow>
            ) : (
              cities.map((city) => (
                <TableRow key={city.id} className={isFetching ? "opacity-60" : ""}>
                  <TableCell className="font-medium">{city.nome || <span className="text-muted-foreground italic">—</span>}</TableCell>
                  <TableCell>
                    {city.estado
                      ? <Badge variant="outline" className="font-mono">{city.estado.uf}</Badge>
                      : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="font-mono">{city.codigoIbge || "—"}</TableCell>
                  <TableCell className="text-right">{city.ibsMun?.toFixed(2) ?? "0.00"}%</TableCell>
                  <TableCell className="text-center">{city.isAtivo ? "Sim" : "Não"}</TableCell>
                  <TableCell>
                    {podeEditar && (
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(city)}><Edit className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => setDeleteId(city.id)}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Paginação */}
      {total > 0 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            Exibindo {inicio}–{fim} de {total.toLocaleString("pt-BR")} cidades
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              disabled={pagina <= 1}
              onClick={() => setPagina((p) => p - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="px-2">
              {pagina} / {totalPaginas}
            </span>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              disabled={pagina >= totalPaginas}
              onClick={() => setPagina((p) => p + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Dialog criar/editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCity ? "Editar Cidade" : "Nova Cidade"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nome *</Label>
              <Input value={formData.nome} onChange={(e) => setFormData({ ...formData, nome: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Estado</Label>
                <Select
                  value={formData.estadoId?.toString() || ""}
                  onValueChange={(v) => setFormData({ ...formData, estadoId: v ? parseInt(v) : null })}
                >
                  <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                  <SelectContent>
                    {states?.map((s) => (
                      <SelectItem key={s.id} value={s.id.toString()}>{s.uf} — {s.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Código IBGE</Label>
                <Input
                  type="number"
                  value={formData.codigoIbge || ""}
                  onChange={(e) => setFormData({ ...formData, codigoIbge: e.target.value ? parseInt(e.target.value) : null })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>IBS Municipal (%)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formData.ibsMun ?? 0}
                onChange={(e) => setFormData({ ...formData, ibsMun: parseFloat(e.target.value) || 0 })}
                placeholder="0.00"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={formData.isAtivo ?? true} onCheckedChange={(checked) => setFormData({ ...formData, isAtivo: checked })} />
              <Label>Ativo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSubmit} disabled={!formData.nome || isCreating || isUpdating}>
              {isCreating || isUpdating ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog excluir */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>Tem certeza que deseja excluir esta cidade?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={isDeleting}>Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
